/**
 * ODL-specific DataGridConfig for the entities grid machinery.
 *
 * Wires the generic grid to the ODL API layer: fetchResourceList for list
 * fetches, fetchResourceStats for stat-card numbers, the entitiesApi RTK Query
 * endpoint for spec discovery, references.js for cross-entity link resolution,
 * toJsonApiParams for the odl-app JSON:API wire format, and useUserPreference
 * for filter persistence. Mounted once via DataGridConfigProvider in
 * src/app/index.js.
 *
 * Contains:
 * - odlDataGridConfig — full ODL config object passed to DataGridConfigProvider
 */

import { skipToken } from '@reduxjs/toolkit/query';
import { t } from 'i18next';

import { fetchResourceList, fetchResourceStats } from '@sophocles/api/odlApp';
import { BASE_ACTION_ICON_REGISTRY, resolveActionIcon } from '@sophocles/data-grids';
import { toJsonApiParams } from '@sophocles/data-grids/datasource/params';
import { BASE_STAT_CARD_RENDERERS, resolveStatCardRenderer } from '@sophocles/data-grids/grid/statCardRenderers';
import useUserPreference from '@sophocles/hooks/useUserPreference';

import { entitiesApi } from './entitiesApi';
import { refLabel, refRoute } from './references';

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

/**
 * Fetches a paginated odl_app list resource.
 *
 * Path must already carry the `odl-app/` prefix and the resource segment
 * (e.g. `odl-app/sites/1/cameras`). EntityGrid builds this from basePath
 * before calling config.fetchFn.
 *
 * @param {string} path  Full API path including `odl-app/` prefix.
 * @param {object} params  Serialized query params (JSON:API wire format).
 * @returns {Promise<{data: object[], meta: object}>}
 */
async function odlFetchFn(path, params) {
  const envelope = (await fetchResourceList(path, params)) ?? {};
  return { data: envelope.data ?? [], meta: envelope.meta ?? {} };
}

// ---------------------------------------------------------------------------
// Spec query hook
// ---------------------------------------------------------------------------

/**
 * RTK Query hook wrapper for grid-spec discovery. Named with a `use` prefix
 * so the react-hooks linter identifies it as a hook at its call sites.
 *
 * @param {string|null|undefined} path  odl_app resource path without trailing slash.
 * @param {object} params  Serialized grid-scope query params.
 * @returns {{ data: object|undefined, isLoading: boolean, isError: boolean }}
 */
function useOdlSpecQuery(path, params) {
  return entitiesApi.endpoints.getGridSpecFor.useQuery(path ? { path, params } : skipToken);
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

/** ODL boolean label formatter. */
const odlBooleanFormatter = v => (v ? t('general.yes', 'Yes') : t('general.no', 'No'));

/** ODL datetime formatter. Override here when timezone-aware formatting is added. */
const odlDatetimeFormatter = v => {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

// ---------------------------------------------------------------------------
// ODL config
// ---------------------------------------------------------------------------

/**
 * The ODL grid dependency config. Passed to DataGridConfigProvider at the app
 * root; all EntityGrid instances in the tree read their dependencies from this
 * object via useDataGridConfig().
 */
export const odlDataGridConfig = {
  fetchFn: odlFetchFn,
  fetchStatsFn: fetchResourceStats,
  useSpecQuery: useOdlSpecQuery,
  resolveReference: ref => ({ label: refLabel(ref), route: refRoute(ref) }),
  paramSerializer: toJsonApiParams,
  usePreferenceStore: useUserPreference,
  translate: t,
  formatters: {
    boolean: odlBooleanFormatter,
    datetime: odlDatetimeFormatter,
  },
  resolveActionIcon: name => resolveActionIcon(name, BASE_ACTION_ICON_REGISTRY),
  resolveStatCardRenderer: type => resolveStatCardRenderer(type, BASE_STAT_CARD_RENDERERS),
};
